import { IconStarFilled } from "@tabler/icons-react"
import { type FC, lazy, Suspense } from "react"

import type { LandingPageTestimonialsBlock } from "@forento/core/models/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import VideoPreview from "./VideoPreview"

const RichTextPreview = lazy(() => import("@forento/ui/components/RichTextPreview"))

type Testimonial = LandingPageTestimonialsBlock["content"]["testimonials"][number]

const TestimonialsBlockPreview: FC<{ block: LandingPageTestimonialsBlock }> = ({ block }) => {
	if (block.content.testimonials.length === 0) return null

	return (
		<section className="px-6 pt-16 pb-0 sm:pt-20 lg:px-8">
			<div className="mx-auto max-w-7xl">
				{block.content.title && (
					<h2 className="text-landing-foreground mb-12 text-center text-3xl font-bold tracking-tight sm:text-4xl">
						{block.content.title}
					</h2>
				)}
				<div
					className={cn(
						"grid gap-8",
						block.content.testimonials.length === 1 && "mx-auto max-w-2xl grid-cols-1",
						block.content.testimonials.length === 2 && "sm:grid-cols-2",
						block.content.testimonials.length >= 3 && "sm:grid-cols-2 lg:grid-cols-3",
						block.content.testimonials.length >= 4 && "lg:grid-cols-4",
					)}
				>
					{block.content.testimonials.map((testimonial, index) => (
						<TestimonialCard key={index} testimonial={testimonial} />
					))}
				</div>
			</div>
		</section>
	)
}

const TestimonialCard: FC<{ testimonial: Testimonial }> = ({ testimonial }) => (
	<article className="bg-landing-surface ring-landing-border/50 flex flex-col rounded-2xl p-8 shadow-lg ring-1 shadow-gray-900/5">
		{testimonial.rating !== undefined && <StarRating rating={testimonial.rating} />}
		{testimonial.media ? (
			<div className="bg-landing-surface-muted ring-landing-border/70 mb-8 overflow-hidden rounded-xl ring-1">
				<VideoPreview type={testimonial.media.type} videoId={testimonial.media.videoId} />
			</div>
		) : (
			testimonial.quote && (
				<Suspense>
					<RichTextPreview
						value={testimonial.quote}
						className="text-landing-foreground-muted mb-8 text-base leading-7 [&>p]:mb-3 [&>p:last-child]:mb-0"
					/>
				</Suspense>
			)
		)}
		<div className="mt-auto flex items-center gap-4">
			<Avatar testimonial={testimonial} />
			<div className="min-w-0">
				<h3 className="text-landing-foreground text-base font-bold tracking-tight">{testimonial.name}</h3>
				{getByline(testimonial) && (
					<p className="text-landing-foreground-subtle mt-0.5 text-sm">{getByline(testimonial)}</p>
				)}
			</div>
		</div>
	</article>
)

const Avatar: FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
	if (testimonial.avatar?.url) {
		return (
			<img
				src={testimonial.avatar.url}
				alt={testimonial.avatar.alt ?? testimonial.name}
				className="bg-landing-surface-muted ring-landing-border size-12 shrink-0 rounded-full object-cover ring-1"
			/>
		)
	}

	return (
		<div className="bg-landing-foreground text-landing-background flex size-12 shrink-0 items-center justify-center rounded-full text-sm font-bold">
			{getInitials(testimonial.name)}
		</div>
	)
}

const StarRating: FC<{ rating: number }> = ({ rating }) => (
	<div className="mb-5 flex items-center gap-1 text-amber-500" role="img" aria-label={`${rating} out of 5 stars`}>
		{Array.from({ length: rating }, (_, index) => (
			<IconStarFilled key={index} className="size-5" />
		))}
	</div>
)

function getInitials(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean)
	const initials = words
		.slice(0, 2)
		.map(word => word[0])
		.join("")
		.toUpperCase()
	return initials || "?"
}

function getByline(testimonial: Testimonial): string {
	return [testimonial.role, testimonial.company].filter(Boolean).join(", ")
}

export default TestimonialsBlockPreview
